<template>
  <div ref="rootRef" class="relative overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-lg" style="aspect-ratio: 393/660">

    <img
        v-if="url"
        :src="url"
        class="w-full h-full object-cover"
        alt=""
        @error="console.error('Thumbnail load failed', url)"
    />

    <div v-else class="w-full h-full flex items-center justify-center">
      <i :class="loading ? 'pi pi-spin pi-spinner' : 'pi pi-image'" class="text-2xl text-gray-400"/>
    </div>

    <div
        v-if="bundleConfig.badgeId === 'new'"
        class="absolute top-1.5 left-1.5 z-50 pointer-events-none flex items-center gap-1 bg-zinc-950/90 dark:bg-zinc-950 border border-primary/40 shadow-[0_0_15px_hsl(var(--primary)/0.25)] rounded-full px-2 py-0.5"
        style="transform: rotate(-15deg) translateZ(0); transform-origin: center; top: 0.4rem; left: 0; will-change: transform;"
    >
      <i class="pi pi-bolt new-bolt-icon" style="font-size: 9px; color: hsl(var(--primary));"></i>
      <span class="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--modal-accent-text))] dark:text-[hsl(var(--modal-accent-text))]">New</span>
    </div>

    <div
        v-if="bundleConfig.badgeId === 'featured'"
        class="absolute top-1.5 left-1.5 z-50 pointer-events-none flex items-center gap-1 bg-zinc-950/90 dark:bg-zinc-950 border border-primary/40 shadow-[0_0_15px_hsl(var(--primary)/0.25)] rounded-full px-2 py-0.5"
        style="transform: rotate(-15deg) translateZ(0); transform-origin: center; top: 0.6rem; left: 0; will-change: transform;"
    >
      <span class="relative flex items-center justify-center w-2.5 h-2.5">
        <span class="featured-star" style="font-size: 9px; line-height: 1; color: hsl(var(--primary));">★</span>
      </span>
      <span class="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--modal-accent-text))] dark:text-[hsl(var(--modal-accent-text))]">Featured</span>
    </div>

    <div
        v-if="bundleConfig.badgeId === 'recent'"
        class="absolute top-1.5 left-1.5 z-50 pointer-events-none flex items-center gap-1 bg-zinc-950/90 dark:bg-zinc-950 border border-primary/40 shadow-[0_0_15px_hsl(var(--primary)/0.25)] rounded-full px-2 py-0.5"
        style="transform: rotate(-15deg) translateZ(0); transform-origin: center; top: 0.5rem; left: 0;will-change: transform;"
    >
      <i class="pi pi-share-alt recent-share-icon" style="font-size: 8px; color: hsl(var(--primary));"></i>
      <span class="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--modal-accent-text))] dark:text-[hsl(var(--modal-accent-text))]">Recent</span>
    </div>

      <div class="absolute left-0 right-0 flex items-center justify-center pointer-events-none" style="top: 65%">
        <div
            class="rotate-[-20deg] select-none"
            :style="{
          border: '3px double rgba(185,28,28,0.9)',
          borderRadius: '4px',
          padding: '4px 10px',
          color: 'rgba(185,28,28,0.9)',
          fontFamily: '\'Courier New\', Courier, monospace',
          fontWeight: '900',
          fontSize: 'clamp(0.6rem, 2.5vw, 1rem)',
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          background: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(2px)',
          boxShadow: '0 0 0 2px rgba(185,28,28,0.15), inset 0 0 6px rgba(185,28,28,0.2), 0 2px 6px rgba(0,0,0,0.25)',
          textShadow: '0 1px 3px rgba(0,0,0,0.3)',
          filter: isMobile ? 'none' : 'url(#roughen)',
          opacity: '0.95'
        }"
        >
          SAMPLE ONLY
        </div>
      </div>


    <div
        class="absolute bottom-0 left-0 right-0 px-2 py-2"
        style="background: linear-gradient(to top, rgba(0,0,0,0.75), transparent)"
    >
      <div class="text-white text-xs font-semibold truncate">{{ bundleConfig.variantName }}</div>
    </div>

  </div>
</template>

<script setup lang="ts">
import {useBundleThumbnail} from "@/components/betbuilder/betslip-image/useBundleThumbnail";
import {inject, onMounted, onUnmounted, ref} from "vue";
import {BundleConfig} from "@/types/client";
import {KEY_IS_MOBILE} from "@/keys/keys";

const props = defineProps<{
  bundleConfig: BundleConfig
  brandId: string
}>()

const isMobile = inject(KEY_IS_MOBILE)

const rootRef = ref<HTMLElement | null>(null)
const visible = ref(false)

let observer: IntersectionObserver | null = null

onMounted(() => {
  observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          visible.value = true
          observer?.disconnect()
        }
      },
      { threshold: 0.1 }
  )
  if (rootRef.value) observer.observe(rootRef.value)
})

onUnmounted(() => observer?.disconnect())

const {url, loading} = useBundleThumbnail(props.bundleConfig.bundleId, props.bundleConfig.variantId, props.brandId, visible)
</script>
<style scoped>
@keyframes featured-star-sparkle {
  0%, 100% {
    transform: scale(1) rotate(0deg);
    filter: drop-shadow(0 0 2px rgba(250, 204, 21, 0.5));
  }
  50% {
    transform: scale(1.35) rotate(18deg);
    filter: drop-shadow(0 0 6px rgba(250, 204, 21, 0.95));
  }
}

.featured-star {
  display: inline-block;
  animation: featured-star-sparkle 2.2s ease-in-out infinite;
}

@keyframes recent-share-pulse {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 2px hsl(var(--primary) / 0.5));
    opacity: 1;
  }
  50% {
    transform: scale(1.4);
    filter: drop-shadow(0 0 6px hsl(var(--primary) / 0.95));
    opacity: 0.85;
  }
}

.recent-share-icon {
  display: inline-block;
  animation: recent-share-pulse 2.2s ease-in-out infinite;
}

@keyframes new-bolt-pulse {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 2px hsl(var(--primary) / 0.5));
  }
  50% {
    transform: scale(1.4);
    filter: drop-shadow(0 0 6px hsl(var(--primary) / 0.95));
  }
}

.new-bolt-icon {
  display: inline-block;
  animation: new-bolt-pulse 2.2s ease-in-out infinite;
}
</style>